import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { ActiveLeaseInfo } from './ActiveLeaseInfo';
import { SidebarSubLinks } from './SidebarSubLinks';
import AddIcon from '@material-ui/icons/Add';
import CategoryIcon from '@material-ui/icons/Category';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import HomeIcon from '@material-ui/icons/Home';
import FolderSpecialIcon from '@material-ui/icons/FolderSpecial';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CallMergeIcon from '@material-ui/icons/CallMerge';
import MenuIcon from '@material-ui/icons/Menu';
import SettingsIcon from '@material-ui/icons/Settings';

// Link straight to /create/templates: /create redirects there and drops the query string.
// Tags are AND-ed, so a template must carry every tag listed.
const createLink = (...tags: string[]) =>
  `/create/templates?${[
    'filters%5Bkind%5D=template',
    ...tags.map(t => `filters%5Btags%5D=${encodeURIComponent(t)}`),
    'filters%5Buser%5D=all',
  ].join('&')}`;

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // Items we render manually — remove from auto-nav pool to prevent duplicates
      nav.take('page:home');
      nav.take('page:catalog');
      nav.take('page:search');
      nav.take('page:scaffolder');
      nav.take('page:techdocs'); // rendered manually as Docs
      nav.take('page:user-settings'); // rendered manually in Settings group
      nav.take('page:space-manager'); // rendered manually as Space Manager
      // Items to suppress — not wanted in sidebar
      nav.take('page:api-docs');
      nav.take('page:catalog-import');
      nav.take('page:catalog-graph');

      return (
        <Sidebar>
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            <SidebarItem icon={HomeIcon} to="/" text="Home" />
            <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
            <SidebarSubLinks
              links={[
                {
                  title: 'My Active Leases',
                  to: '/catalog?filters%5Bkind%5D=resource&filters%5Btype%5D=aws-sandbox-lease&filters%5Btags%5D=active&filters%5Buser%5D=all',
                },
                {
                  title: 'My Clusters',
                  to: '/catalog?filters%5Bkind%5D=system&filters%5Btype%5D=kubernetes-cluster&filters%5Buser%5D=owned',
                },
                {
                  title: 'My Components',
                  to: '/catalog?filters%5Bkind%5D=component&filters%5Buser%5D=all',
                },
              ]}
            />
            <SidebarItem icon={AddIcon} to="create" text="Create" />
            <SidebarSubLinks
              links={[
                { title: 'AWS Account', to: createLink('aws', 'account') },
                { title: 'Infra', to: createLink('infra') },
                {
                  title: 'Kubernetes Cluster',
                  to: createLink('infra', 'kubernetes'),
                },
              ]}
            />
            <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
            <SidebarItem
              icon={FolderSpecialIcon}
              to="space-manager"
              text="Space Manager"
            />
            <SidebarDivider />
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <ActiveLeaseInfo />
          <SidebarDivider />
          <SidebarItem
            icon={OpenInNewIcon}
            to="https://learning.platform-engineering.com/"
            text="Learning Portal"
            target="_blank"
            rel="noopener noreferrer"
          />
          <SidebarItem
            icon={OpenInNewIcon}
            to="https://d1uosxmjn6smbi.cloudfront.net/"
            text="AWS Sandbox Portal"
            target="_blank"
            rel="noopener noreferrer"
          />
          <SidebarItem
            icon={CallMergeIcon}
            to="https://gitlab.platform-engineering.com/sandboxes/workspace"
            text="Contributing"
            target="_blank"
            rel="noopener noreferrer"
          />
          <SidebarDivider />
          <SidebarItem icon={SettingsIcon} to="/settings" text="Settings" />
        </Sidebar>
      );
    },
  },
});
